import React, { useEffect, useState, useMemo } from 'react';
import {
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogContent,
  IconButton,
  Chip,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';
import { reportsApi } from '../../api/reports';
import { exportToCsv } from './ReportLayout';

interface DesignInfo {
  design_no: string;
  design_image_url: string | null;
}

interface PendingPORow {
  id: string;
  vendor_id: number;
  vendor: string;
  po_number: string;
  po_id: number;
  po_date: string;
  material_type: string;
  material_quality: string | null;
  description: string | null;
  garment_part: string | null;
  uom: string;
  qty_ordered: number;
  qty_fulfilled: number;
  pending_qty: number;
  rate: number;
  pending_amount: number;
  aging_days: number;
  image_url: string | null;
  designs: DesignInfo[];
}

interface VendorOption {
  id: number;
  registered_name: string;
  alias: string | null;
}

function AgingChip({ days }: { days: number }) {
  const color = days > 30 ? 'error' : days > 15 ? 'warning' : 'default';
  return <Chip label={`${days}d`} color={color} size="small" variant="outlined" />;
}

function ImageThumbnail({
  src,
  alt,
  onClick,
}: {
  src: string;
  alt: string;
  onClick: () => void;
}) {
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onClick={onClick}
      sx={{
        width: 48,
        height: 48,
        objectFit: 'cover',
        borderRadius: 1,
        border: '1px solid #ddd',
        cursor: 'pointer',
        '&:hover': { opacity: 0.8, boxShadow: 2 },
      }}
    />
  );
}

export default function POFulfillmentReport() {
  const [rows, setRows] = useState<PendingPORow[]>([]);
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [vendorFilter, setVendorFilter] = useState<number | ''>('');
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportsApi.getPendingPO({
        vendor_id: vendorFilter || undefined,
      });
      setRows(res.data.rows);
      setVendors(res.data.vendors);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [vendorFilter]);

  // Group rows by vendor
  const grouped = useMemo(() => {
    const map = new Map<string, { vendor: string; vendor_id: number; rows: PendingPORow[] }>();
    for (const row of rows) {
      if (!map.has(row.vendor)) {
        map.set(row.vendor, { vendor: row.vendor, vendor_id: row.vendor_id, rows: [] });
      }
      map.get(row.vendor)!.rows.push(row);
    }
    return Array.from(map.values());
  }, [rows]);

  const handleExport = () => {
    const exportRows = rows.map((r) => ({
      Vendor: r.vendor,
      'PO Number': r.po_number,
      'PO Date': new Date(r.po_date).toLocaleDateString('en-IN'),
      'Material Type': r.material_type,
      Quality: r.material_quality || '',
      Description: r.description || '',
      'Garment Part': r.garment_part || '',
      UOM: r.uom,
      'Qty Ordered': r.qty_ordered,
      'Qty Fulfilled': r.qty_fulfilled,
      'Pending Qty': r.pending_qty,
      Rate: r.rate,
      'Pending Amount': r.pending_amount,
      'Aging (Days)': r.aging_days,
      'Design No': r.designs.map((d) => d.design_no).join(', '),
    }));
    exportToCsv('pending-po-report.csv', exportRows);
  };

  const grandTotalOrdered = rows.reduce((s, r) => s + r.qty_ordered, 0);
  const grandTotalPending = rows.reduce((s, r) => s + r.pending_qty, 0);
  const grandTotalAmount = rows.reduce((s, r) => s + r.pending_amount, 0);

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        sx={{ mb: 2, flexWrap: 'wrap' }}
      >
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Pending PO Report
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Vendor</InputLabel>
          <Select
            value={vendorFilter}
            label="Vendor"
            onChange={(e) => setVendorFilter(e.target.value as number | '')}
          >
            <MenuItem value="">All Vendors</MenuItem>
            {vendors.map((v) => (
              <MenuItem key={v.id} value={v.id}>
                {v.registered_name || v.alias}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={loading || rows.length === 0}
        >
          Export CSV
        </Button>
      </Stack>

      <Paper sx={{ p: 0, overflow: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Typography sx={{ p: 3, textAlign: 'center' }} color="text.secondary">
            No pending PO items found.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>PO Number</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>PO Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Design</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>UOM</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Ordered</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Fulfilled</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Pending</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Rate</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Pending Amt</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Aging</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grouped.map((group) => {
                  const vendorOrdered = group.rows.reduce((s, r) => s + r.qty_ordered, 0);
                  const vendorPending = group.rows.reduce((s, r) => s + r.pending_qty, 0);
                  const vendorAmount = group.rows.reduce((s, r) => s + r.pending_amount, 0);

                  return (
                    <React.Fragment key={group.vendor}>
                      {/* Vendor header row */}
                      <TableRow>
                        <TableCell
                          colSpan={12}
                          sx={{
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            py: 1,
                          }}
                        >
                          {group.vendor}
                        </TableCell>
                      </TableRow>

                      {/* Item rows */}
                      {group.rows.map((row) => {
                        const itemDesc = [
                          row.material_type,
                          row.material_quality,
                          row.description,
                          row.garment_part ? `(${row.garment_part})` : '',
                        ]
                          .filter(Boolean)
                          .join(' - ');

                        return (
                          <TableRow key={row.id} hover>
                            <TableCell>{row.po_number}</TableCell>
                            <TableCell>
                              {new Date(row.po_date).toLocaleDateString('en-IN')}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 200 }}>
                              <Typography variant="body2" noWrap title={itemDesc}>
                                {itemDesc}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {row.image_url ? (
                                <ImageThumbnail
                                  src={row.image_url}
                                  alt="Item"
                                  onClick={() => setPreviewImage(row.image_url)}
                                />
                              ) : (
                                <Typography variant="caption" color="text.disabled">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {row.designs.length > 0 ? (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                  {row.designs.map((d) => (
                                    <Stack
                                      key={d.design_no}
                                      direction="row"
                                      spacing={0.5}
                                      alignItems="center"
                                    >
                                      {d.design_image_url && (
                                        <ImageThumbnail
                                          src={d.design_image_url}
                                          alt={d.design_no}
                                          onClick={() =>
                                            setPreviewImage(d.design_image_url)
                                          }
                                        />
                                      )}
                                      <Typography variant="caption" fontWeight={600}>
                                        {d.design_no}
                                      </Typography>
                                    </Stack>
                                  ))}
                                </Stack>
                              ) : (
                                <Typography variant="caption" color="text.disabled">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{row.uom}</TableCell>
                            <TableCell align="right">{row.qty_ordered}</TableCell>
                            <TableCell align="right">{row.qty_fulfilled}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {row.pending_qty}
                            </TableCell>
                            <TableCell align="right">{row.rate.toLocaleString('en-IN')}</TableCell>
                            <TableCell align="right">
                              {row.pending_amount.toLocaleString('en-IN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </TableCell>
                            <TableCell align="center">
                              <AgingChip days={row.aging_days} />
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {/* Vendor subtotal row */}
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          sx={{ fontWeight: 700, bgcolor: 'grey.100', borderBottom: 2, borderColor: 'grey.400' }}
                        >
                          Subtotal — {group.vendor}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, bgcolor: 'grey.100', borderBottom: 2, borderColor: 'grey.400' }}
                        >
                          {vendorOrdered}
                        </TableCell>
                        <TableCell sx={{ bgcolor: 'grey.100', borderBottom: 2, borderColor: 'grey.400' }} />
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, bgcolor: 'grey.100', borderBottom: 2, borderColor: 'grey.400' }}
                        >
                          {vendorPending}
                        </TableCell>
                        <TableCell sx={{ bgcolor: 'grey.100', borderBottom: 2, borderColor: 'grey.400' }} />
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, bgcolor: 'grey.100', borderBottom: 2, borderColor: 'grey.400' }}
                        >
                          {vendorAmount.toLocaleString('en-IN', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </TableCell>
                        <TableCell sx={{ bgcolor: 'grey.100', borderBottom: 2, borderColor: 'grey.400' }} />
                      </TableRow>
                    </React.Fragment>
                  );
                })}

                {/* Grand total row */}
                <TableRow>
                  <TableCell
                    colSpan={6}
                    sx={{ fontWeight: 700, fontSize: '0.95rem', bgcolor: 'grey.300', borderTop: 2 }}
                  >
                    Grand Total
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700, fontSize: '0.95rem', bgcolor: 'grey.300', borderTop: 2 }}
                  >
                    {grandTotalOrdered}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'grey.300', borderTop: 2 }} />
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700, fontSize: '0.95rem', bgcolor: 'grey.300', borderTop: 2 }}
                  >
                    {grandTotalPending}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'grey.300', borderTop: 2 }} />
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700, fontSize: '0.95rem', bgcolor: 'grey.300', borderTop: 2 }}
                  >
                    {grandTotalAmount.toLocaleString('en-IN', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </TableCell>
                  <TableCell sx={{ bgcolor: 'grey.300', borderTop: 2 }} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Image preview dialog */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="md"
      >
        <DialogContent sx={{ p: 1, position: 'relative' }}>
          <IconButton
            onClick={() => setPreviewImage(null)}
            sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
          {previewImage && (
            <Box
              component="img"
              src={previewImage}
              alt="Preview"
              sx={{ maxWidth: '100%', maxHeight: '80vh', display: 'block' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
